import React, { DetailedHTMLProps, InputHTMLAttributes, Ref } from 'react';

import clsx from 'clsx';

import useRandomId from 'hooks/randomIdFallback';
import { removeWhiteSpace } from 'components/common/TextInput/textInput.utils';

import styles from './TextInput.module.scss';
import { CopyIcon, TooltipIcon } from '../Icons/Icons';
import Tooltip from '../Tooltip/Tooltip';

export type HTMLInputProps = DetailedHTMLProps<InputHTMLAttributes<HTMLInputElement>, HTMLInputElement>;

interface TextInputProps {
  labelTitle?: string;
  errorMessage?: string;
  error?: boolean;
  value?: string;
  containerClassName?: string;
  useRemoveInput?: boolean;
  onRemoveInput?: () => void;
  useRequiredSymbol?: boolean;
  useCopyInput?: boolean;
  onCopyInput?: () => void;
  tooltipTitle?: string;
}

export type TextInputSingleLineProps = TextInputProps & HTMLInputProps & { inputRef?: Ref<HTMLInputElement> };

const getFieldElement = (props: TextInputSingleLineProps): JSX.Element => {
  const {
    className,
    errorMessage,
    value: rawValue,
    inputRef,
    error,
    ...fieldProps
  } = props as TextInputSingleLineProps;
  const value = removeWhiteSpace(rawValue);
  const inputClassName = clsx(styles.input, (errorMessage || error) && styles.inputError, className);

  return <input className={inputClassName} value={value} {...fieldProps} ref={inputRef} />;
};

const TextInput = ({
  id: providedId,
  labelTitle,
  errorMessage,
  containerClassName,
  onRemoveInput,
  useRemoveInput = false,
  useRequiredSymbol = false,
  useCopyInput = false,
  onCopyInput,
  tooltipTitle,
  ...restProps
}: TextInputSingleLineProps) => {
  const id = useRandomId(providedId);

  const fieldElement = getFieldElement({
    errorMessage: errorMessage,
    ...restProps,
    id: id,
  });

  return (
    <div className={clsx(styles.inputContainer, containerClassName)}>
      <div className={styles.labelWithLink}>
        <div className={styles.labelWrap}>
          <label htmlFor={id} className={styles.label}>
            {labelTitle}
            {useRequiredSymbol && <div className={styles.requiredInputLabel}>*</div>}
          </label>
          {tooltipTitle && (
            <Tooltip title={tooltipTitle}>
              <TooltipIcon />
            </Tooltip>
          )}
        </div>
        {useRemoveInput && (
          <div onClick={onRemoveInput} className={styles.removeInputButton}>
            Remove
          </div>
        )}
      </div>
      <div
        className={clsx(styles.fieldContainer, {
          [styles.fieldContainerWithCopy]: useCopyInput,
        })}
      >
        {fieldElement}
        {useCopyInput && (
          <div onClick={onCopyInput} className={styles.copyInputButton}>
            <CopyIcon />
          </div>
        )}
      </div>
      {errorMessage && <p className={styles.errorNotification}>{errorMessage}</p>}
    </div>
  );
};

export default TextInput;
